import React, { FC } from 'react';
import { CustomDateInputProps } from './CustomDateInputProps';
import { FormControl, FormHelperText } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import useTranslation from '../../hooks/useTranslation';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { returnDisabledStatus } from '../../utils/fieldEditingRules';
import InputField from '@ingka/input-field';
import { format } from 'date-fns';

const CustomDateInput: FC<CustomDateInputProps> = ({
  name,
  disabled,
  customOnChange,
  required,
  dataTestID,
  label,
}) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();
  const t = useTranslation();
  const disabledFields = watch('disabledFields');

  const addOffset = (date) => {
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() + offset * 60 * 1000);
  };

  return (
    <Controller
      defaultValue={required && addOffset(new Date())}
      render={({ field: { onChange, value } }) => {
        const isDisabled =
          disabled || returnDisabledStatus(disabledFields, name);

        return (
          <FormControl
            fullWidth
            required={required}
            data-testid={dataTestID}
            error={!!errors[name]?.message}
          >
            {isDisabled ? (
              <InputField
                className="read-only-input"
                id={dataTestID}
                type="text"
                label={`${t(label)}${required ? ' *' : ''}`}
                value={value ? format(new Date(value), 'yyyy/MM/dd') : ''}
                data-testid={dataTestID}
                readOnly
              />
            ) : (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  sx={{
                    '& .MuiInputLabel-root': {
                      'z-index': 0,
                    },
                  }}
                  label={`${t(label)}${required ? ' *' : ''}`}
                  onChange={(value) => {
                    onChange(value);
                    customOnChange && customOnChange(value);
                  }}
                  value={(value ? addOffset(new Date(value)) : null) as never}
                  slotProps={{
                    field: { clearable: true },
                  }}
                  format="yyyy/MM/dd"
                />
              </LocalizationProvider>
            )}
            {!!errors[name]?.message && (
              <FormHelperText>{errors[name]?.message}</FormHelperText>
            )}
          </FormControl>
        );
      }}
      control={control}
      name={name}
    />
  );
};

export default CustomDateInput;
